/**
 * Country Detection Loader
 *
 * Loading elegante mientras se detecta el país del usuario.
 * Diseño premium con animaciones fluidas y branding de Ana Fernández.
 *
 * UX optimizada:
 * - Animación de 500-800ms (tiempo de detección API)
 * - Skeleton loader con gradientes
 * - Transición suave a checkout
 */

import { Globe, Loader2, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface CountryDetectionLoaderProps {
  /** Mensaje personalizado (opcional) */
  message?: string;
  /** Mostrar país detectado cuando esté disponible */
  detectedCountry?: string | null;
  /** Confianza de la detección */
  confidence?: "high" | "medium" | "low" | "unknown";
  /** Ciudad detectada (opcional, desde Cloudflare Worker) */
  city?: string | null;
}

export function CountryDetectionLoader({
  message,
  detectedCountry,
  confidence,
  city,
}: CountryDetectionLoaderProps) {
  const { t } = useTranslation();
  const displayMessage = message ?? t("countryLoader.defaultMessage");
  const [dots, setDots] = useState("");
  const [progress, setProgress] = useState(0);

  // Animación de dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  // Animación de progreso (simula detección)
  useEffect(() => {
    // Progreso rápido inicial
    const timer1 = setTimeout(() => setProgress(30), 100);
    const timer2 = setTimeout(() => setProgress(60), 300);

    // Si ya tenemos país, completar rápido
    if (detectedCountry) {
      const timer3 = setTimeout(() => setProgress(100), 400);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }

    // Si no, progreso más lento
    const timer3 = setTimeout(() => setProgress(80), 600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [detectedCountry]);

  // Determinar color según confianza
  const getConfidenceColor = () => {
    switch (confidence) {
      case "high":
        return "from-green-500 to-emerald-500";
      case "medium":
        return "from-blue-500 to-indigo-500";
      case "low":
        return "from-yellow-500 to-orange-500";
      default:
        return "from-purple-500 to-pink-500";
    }
  };

  return (
    <div className="w-full h-dvh flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-purple-950/95 to-black text-white overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-blue-500/10 animate-pulse" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-blob" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-6 px-6 max-w-md mx-auto animate-fade-in">
        {/* Icon with pulsing effect */}
        <div className="relative">
          <div
            className={`absolute inset-0 bg-gradient-to-r ${getConfidenceColor()} rounded-full blur-2xl opacity-50 animate-pulse`}
          />
          <div
            className={`relative w-20 h-20 bg-gradient-to-br ${getConfidenceColor()} rounded-full flex items-center justify-center shadow-2xl`}
          >
            {detectedCountry ? (
              <MapPin className="w-10 h-10 text-white animate-bounce" />
            ) : (
              <Globe className="w-10 h-10 text-white animate-spin-slow" />
            )}
          </div>
        </div>

        {/* Main message */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-white">
            {displayMessage}
            <span className="inline-block w-8 text-left">{dots}</span>
          </h2>

          {detectedCountry && (
            <div className="flex flex-col items-center justify-center gap-1 animate-fade-in">
              <div className="flex items-center gap-2 text-sm text-white/70">
                <MapPin className="w-4 h-4" />
                <span>{getCountryDisplay(detectedCountry)}</span>
              </div>
              {city && <div className="text-xs text-white/50">{city}</div>}
            </div>
          )}

          {confidence && confidence !== "unknown" && (
            <div className="text-xs text-white/50">
              {confidence === "high"
                ? t("countryLoader.confidenceHigh")
                : confidence === "medium"
                  ? t("countryLoader.confidenceMedium")
                  : t("countryLoader.confidenceLow")}
            </div>
          )}
        </div>

        {/* Loading bar with progress */}
        <div className="w-full max-w-xs space-y-2">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${getConfidenceColor()} rounded-full transition-all duration-300 ease-out`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {progress > 0 && (
            <div className="text-center text-white/40 text-xs animate-fade-in">
              {progress < 40
                ? t("countryLoader.progressDetect")
                : progress < 70
                  ? t("countryLoader.progressVerify")
                  : progress < 100
                    ? t("countryLoader.progressPrep")
                    : t("countryLoader.progressRedirect")}
            </div>
          )}
        </div>

        {/* Subtle footer */}
        <p className="text-white/40 text-xs text-center">
          {t("countryLoader.footer")}
        </p>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes loading-bar {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-loading-bar {
          animation: loading-bar 1.5s ease-in-out;
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
}

/**
 * Mini loader inline (para usar dentro de botones o modales)
 */
export function CountryDetectionMiniLoader() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-white/70">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span className="text-sm">{t("countryLoader.mini")}</span>
    </div>
  );
}

/**
 * Mapeo de códigos de país a nombres con banderas
 */
function getCountryDisplay(countryCode: string): string {
  const countryMap: Record<string, string> = {
    // LATAM
    AR: "🇦🇷 Argentina",
    MX: "🇲🇽 México",
    CL: "🇨🇱 Chile",
    CO: "🇨🇴 Colombia",
    BR: "🇧🇷 Brasil",
    PE: "🇵🇪 Perú",
    UY: "🇺🇾 Uruguay",
    PY: "🇵🇾 Paraguay",
    BO: "🇧🇴 Bolivia",
    EC: "🇪🇨 Ecuador",
    VE: "🇻🇪 Venezuela",
    CR: "🇨🇷 Costa Rica",
    PA: "🇵🇦 Panamá",
    GT: "🇬🇹 Guatemala",
    DO: "🇩🇴 República Dominicana",

    // Europa
    ES: "🇪🇸 España",
    DE: "🇩🇪 Alemania",
    FR: "🇫🇷 Francia",
    IT: "🇮🇹 Italia",
    PT: "🇵🇹 Portugal",
    GB: "🇬🇧 Reino Unido",
    NL: "🇳🇱 Países Bajos",
    BE: "🇧🇪 Bélgica",
    CH: "🇨🇭 Suiza",
    AT: "🇦🇹 Austria",

    // Norteamérica
    US: "🇺🇸 Estados Unidos",
    CA: "🇨🇦 Canadá",
  };

  return (
    countryMap[countryCode.toUpperCase()] || `${countryCode.toUpperCase()}`
  );
}
